/**
 * Tableau de bord : indicateurs de la période (avec la période précédente pour les vues),
 * vues par jour, statistiques de chaque élément, derniers commentaires et photos.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchInsights, photoThumbUrl, type InsightElement } from '../api'
import { Thumb } from '../components/Thumb'
import { useOrg } from '../utils/orgContext'
import { ViewsChart } from '../components/ViewsChart'
import { StatusPill, TypeIcon } from '../components/badges'
import { formatDate, formatNumber } from '../utils/format'
import { editLocationPath } from '../utils/routes'

const PERIODS = [7, 30, 90, 365] as const
const TOP_ROWS = 10
type SortKey = 'name' | 'views' | 'like_count' | 'to_visit_count' | 'visited_count' | 'comment_count' | 'photo_count'

function Delta({ now, prev }: { now: number; prev: number }) {
  const { t } = useTranslation('gestion')
  if (!prev && !now) return null
  if (!prev) return <span className="g-kpi__delta g-kpi__delta--up">{t('tableau.nouveau')}</span>
  const pct = Math.round(((now - prev) / prev) * 100)
  const cls = pct > 0 ? 'g-kpi__delta--up' : pct < 0 ? 'g-kpi__delta--down' : ''
  return (
    <span className={`g-kpi__delta ${cls}`} title={t('tableau.periode_precedente', { n: formatNumber(prev) })}>
      {pct > 0 ? '+' : ''}
      {pct} %
    </span>
  )
}

export function DashboardPage() {
  const { t } = useTranslation('gestion')
  const { orgId, orgName } = useOrg()
  const [days, setDays] = useState<number>(30)
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'views', desc: true })
  const [showAll, setShowAll] = useState(false)
  const { data, isLoading, error } = useQuery({
    queryKey: ['insights', orgId, days],
    queryFn: ({ signal }) => fetchInsights(orgId, days, signal),
  })

  const rows = useMemo(() => {
    const list = [...(data?.elements ?? [])]
    list.sort((a, b) => {
      const av = a[sort.key] as number | string
      const bv = b[sort.key] as number | string
      const cmp = typeof av === 'string' ? av.localeCompare(String(bv)) : (av as number) - (bv as number)
      return sort.desc ? -cmp : cmp
    })
    return list
  }, [data, sort])

  const header = (key: SortKey, label: string) => (
    <th
      scope="col"
      className={`g-table__sortable${sort.key === key ? ' g-table__sortable--active' : ''}`}
      onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }))}
      aria-sort={sort.key === key ? (sort.desc ? 'descending' : 'ascending') : 'none'}
    >
      {label}
      {sort.key === key && <span className={`mdi ${sort.desc ? 'mdi-arrow-down' : 'mdi-arrow-up'}`} aria-hidden />}
    </th>
  )

  const totals = data?.totals
  const kpis = totals
    ? [
        { icon: 'mdi-eye-outline', label: t('stats.vues'), value: totals.views, delta: <Delta now={totals.views} prev={totals.views_prev} /> },
        { icon: 'mdi-heart-outline', label: t('stats.jaime'), value: totals.like_count },
        { icon: 'mdi-bookmark-outline', label: t('stats.a_visiter'), value: totals.to_visit_count },
        { icon: 'mdi-check-circle-outline', label: t('stats.visite'), value: totals.visited_count },
        { icon: 'mdi-comment-text-outline', label: t('stats.commentaires'), value: totals.comment_count },
        { icon: 'mdi-image-outline', label: t('stats.photos'), value: totals.photo_count },
      ]
    : []

  return (
    <div className="g-page">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('tableau.titre')}</h1>
          <p className="g-muted">{orgName}</p>
        </div>
        <div className="g-segmented" role="group" aria-label={t('tableau.periode')}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`g-segmented__item${days === p ? ' g-segmented__item--active' : ''}`}
              onClick={() => setDays(p)}
              aria-pressed={days === p}
            >
              {t(`tableau.jours_${p}`)}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="g-alert g-alert--error">{(error as Error).message}</p>}

      <section className="g-kpis" aria-busy={isLoading}>
        {(isLoading ? Array.from({ length: 6 }, () => null) : kpis).map((k, i) => (
          <div key={i} className={`g-kpi${k ? '' : ' g-kpi--loading'}`}>
            {k && (
              <>
                <span className={`mdi ${k.icon} g-kpi__icon`} aria-hidden />
                <span className="g-kpi__label">{k.label}</span>
                <span className="g-kpi__value">{formatNumber(k.value)}</span>
                {k.delta}
              </>
            )}
          </div>
        ))}
      </section>
      <p className="g-hint">{t('tableau.note_compteurs')}</p>

      <div className="g-grid-2">
        <section className="g-card">
          <h2 className="g-card__title">{t('tableau.vues_par_jour')}</h2>
          {data ? <ViewsChart points={data.views_by_day} /> : <div className="g-chart g-chart--loading" />}
        </section>
        <section className="g-card">
          <h2 className="g-card__title">{t('tableau.actions_rapides')}</h2>
          <div className="g-quick">
            <Link className="g-quick__item" to="lieux/nouveau?type=refuge">
              <span className="mdi mdi-home-plus-outline" aria-hidden /> {t('lieux.ajouter_refuge')}
            </Link>
            <Link className="g-quick__item" to="lieux/nouveau?type=camping">
              <span className="mdi mdi-tent" aria-hidden /> {t('lieux.ajouter_camping')}
            </Link>
            <Link className="g-quick__item" to="carte">
              <span className="mdi mdi-map-marker-plus-outline" aria-hidden /> {t('tableau.editer_carte')}
            </Link>
            <Link className="g-quick__item" to="fiche#medias">
              <span className="mdi mdi-image-plus-outline" aria-hidden /> {t('tableau.ajouter_photos')}
            </Link>
          </div>
        </section>
      </div>

      <section className="g-card">
        <div className="g-card__head">
          <h2 className="g-card__title">{t('tableau.par_element')}</h2>
          <Link to="lieux" className="g-link">
            {t('tableau.tous_les_lieux')}
          </Link>
        </div>
        <div className="g-table-wrap">
          <table className="g-table">
            <thead>
              <tr>
                {header('name', t('tableau.element'))}
                {header('views', t('stats.vues'))}
                {header('like_count', t('stats.jaime'))}
                {header('to_visit_count', t('stats.a_visiter'))}
                {header('visited_count', t('stats.visite'))}
                {header('comment_count', t('stats.commentaires'))}
                {header('photo_count', t('stats.photos'))}
              </tr>
            </thead>
            <tbody>
              {(showAll ? rows : rows.slice(0, TOP_ROWS)).map((el: InsightElement) => (
                <tr key={`${el.type}-${el.id}`}>
                  <td>
                    <Link to={el.type === 'organization' ? 'fiche' : editLocationPath(orgId, el.type, el.id)} className="g-table__name">
                      <TypeIcon type={el.type} />
                      <span>{el.name}</span>
                    </Link>
                    {el.status !== 'active' && <StatusPill status={el.status} />}
                  </td>
                  <td className="g-num">
                    {formatNumber(el.views)}
                    {el.views_prev > 0 && <span className="g-num__prev"> / {formatNumber(el.views_prev)}</span>}
                  </td>
                  <td className="g-num">{formatNumber(el.like_count)}</td>
                  <td className="g-num">{formatNumber(el.to_visit_count)}</td>
                  <td className="g-num">{formatNumber(el.visited_count)}</td>
                  <td className="g-num">{formatNumber(el.comment_count)}</td>
                  <td className="g-num">{formatNumber(el.photo_count)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > TOP_ROWS && (
          <button type="button" className="btn btn--sm g-table__more" onClick={() => setShowAll((v) => !v)}>
            {showAll ? t('tableau.afficher_moins') : t('tableau.afficher_tout', { count: rows.length })}
          </button>
        )}
        <p className="g-hint">{t('tableau.note_vues')}</p>
      </section>

      <div className="g-grid-2">
        <section className="g-card">
          <div className="g-card__head">
            <h2 className="g-card__title">{t('tableau.derniers_commentaires')}</h2>
            <Link to="avis" className="g-link">
              {t('tableau.tout_voir')}
            </Link>
          </div>
          {data && data.recent_comments.length === 0 && <p className="g-muted">{t('avis.aucun')}</p>}
          <ul className="g-comments">
            {data?.recent_comments.slice(0, 5).map((c) => (
              <li key={c.id} className="g-comment">
                <div className="g-comment__meta">
                  <strong>{c.author}</strong> · {c.location_name} · {formatDate(c.created_at)}
                  {c.status === 'pending' && <span className="g-badge g-badge--warn">{t('avis.en_moderation')}</span>}
                </div>
                <p className="g-comment__body">{c.body}</p>
              </li>
            ))}
          </ul>
        </section>
        <section className="g-card">
          <div className="g-card__head">
            <h2 className="g-card__title">{t('tableau.dernieres_photos')}</h2>
            <Link to="photos" className="g-link">
              {t('tableau.tout_voir')}
            </Link>
          </div>
          {data && data.recent_photos.length === 0 && <p className="g-muted">{t('photos.aucune')}</p>}
          <div className="g-thumbs">
            {data?.recent_photos.slice(0, 8).map((p) => (
              <figure key={p.id} className="g-thumb" title={`${p.location_name} · ${p.author}`}>
                <Thumb src={photoThumbUrl(p.id)} alt={p.location_name} />
              </figure>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}
